/**
 * Supported Languages for Gemini Live API
 * 
 * Based on official Gemini documentation:
 * @see https://docs.cloud.google.com/vertex-ai/generative-ai/docs/live-api/configure-language-voice
 * 
 * The Gemini Live API supports 24 languages with configurable voices.
 * For Live models like gemini-3.1-flash-live-preview, the model can switch
 * between languages naturally but can also be restricted via system instructions.
 * 
 * @module lib/languages
 */

export interface SupportedLanguage {
    code: string;      // BCP-47 language code
    name: string;      // Display name
    nativeName: string; // Name in native script
    /**
     * ISO 3166-1 alpha-2 country code used to pick a flag badge. Omitted for
     * region-neutral languages (e.g. Arabic Generic) so we never show a flag
     * that misrepresents the language.
     */
    countryCode?: string;
}

/**
 * Complete list of languages supported by Gemini Live API
 * Based on the official documentation
 */
export const SUPPORTED_LANGUAGES: SupportedLanguage[] = [
    // English variants
    { code: 'en-US', name: 'English (US)', nativeName: 'English', countryCode: 'US' },

    // European languages
    { code: 'fr-FR', name: 'French (France)', nativeName: 'Français', countryCode: 'FR' },
    { code: 'de-DE', name: 'German', nativeName: 'Deutsch', countryCode: 'DE' },
    { code: 'it-IT', name: 'Italian', nativeName: 'Italiano', countryCode: 'IT' },
    { code: 'es-US', name: 'Spanish (US)', nativeName: 'Español', countryCode: 'ES' },
    { code: 'pt-BR', name: 'Portuguese (Brazil)', nativeName: 'Português', countryCode: 'BR' },
    { code: 'nl-NL', name: 'Dutch', nativeName: 'Nederlands', countryCode: 'NL' },
    { code: 'pl-PL', name: 'Polish', nativeName: 'Polski', countryCode: 'PL' },
    { code: 'ru-RU', name: 'Russian', nativeName: 'Русский', countryCode: 'RU' },
    { code: 'tr-TR', name: 'Turkish', nativeName: 'Türkçe', countryCode: 'TR' },

    // Asian languages
    { code: 'ja-JP', name: 'Japanese', nativeName: '日本語', countryCode: 'JP' },
    { code: 'ko-KR', name: 'Korean', nativeName: '한국어', countryCode: 'KR' },
    { code: 'th-TH', name: 'Thai', nativeName: 'ภาษาไทย', countryCode: 'TH' },
    { code: 'vi-VN', name: 'Vietnamese', nativeName: 'Tiếng Việt', countryCode: 'VN' },
    { code: 'id-ID', name: 'Indonesian', nativeName: 'Bahasa Indonesia', countryCode: 'ID' },

    // South Asian languages
    { code: 'hi-IN', name: 'Hindi', nativeName: 'हिन्दी', countryCode: 'IN' },
    { code: 'bn-BD', name: 'Bangla', nativeName: 'বাংলা', countryCode: 'BD' },
    { code: 'ta-IN', name: 'Tamil', nativeName: 'தமிழ்', countryCode: 'IN' },
    { code: 'te-IN', name: 'Telugu', nativeName: 'తెలుగు', countryCode: 'IN' },

    // Middle Eastern (region-neutral: no single-country flag)
    { code: 'ar-XA', name: 'Arabic (Generic)', nativeName: 'العربية' },
];

/**
 * Default language for interviews
 */
export const DEFAULT_INTERVIEW_LANGUAGE = 'en-US';

/**
 * Default language for manager output
 */
export const DEFAULT_OUTPUT_LANGUAGE = 'en-US';

/**
 * Get display name for a language code
 */
export function getLanguageName(code: string): string {
    const lang = SUPPORTED_LANGUAGES.find(l => l.code === code);
    return lang ? lang.name : code;
}

/**
 * Get full language info for a language code
 */
export function getLanguageInfo(code: string): SupportedLanguage | undefined {
    return SUPPORTED_LANGUAGES.find(l => l.code === code);
}

/**
 * Short display code for the compact selector pill, e.g. 'en-US' -> 'EN'.
 * Uses the base language subtag (not the country) so each language reads as
 * itself (Tamil -> 'TA', Hindi -> 'HI') even when they share a flag.
 */
export function getLanguageShortCode(code: string): string {
    return getBaseLanguage(code).toUpperCase();
}

/**
 * Check if a language code is supported
 */
export function isLanguageSupported(code: string): boolean {
    return SUPPORTED_LANGUAGES.some(l => l.code === code);
}

/**
 * Get the base language code (e.g., 'en' from 'en-US')
 */
export function getBaseLanguage(code: string): string {
    return code.split('-')[0];
}

/**
 * Find the best matching language for a base code
 * e.g., 'en' -> 'en-US', 'es' -> 'es-US'
 */
export function findBestMatch(baseCode: string): SupportedLanguage | undefined {
    // First, try exact match
    const exact = SUPPORTED_LANGUAGES.find(l => l.code === baseCode);
    if (exact) return exact;

    // Then, find any variant
    return SUPPORTED_LANGUAGES.find(l => getBaseLanguage(l.code) === baseCode.toLowerCase());
}
